import { onBeforeUnmount, onMounted, ref, watch } from 'vue';

// On touch screens the sidebar folds into a thin strip. A tap on the strip, or a swipe from the
// left edge, opens the menu; a swiping finger pulls the panel out with it. The open menu then
// works with ordinary taps. A tap outside it, a swipe to the left or Escape folds it back.
export const MENU_STRIP_QUERY = '(hover: none) and (pointer: coarse)';

// Used when the panel has no layout yet (it is 220px wide).
const PANEL_WIDTH = 220;
// Movement that tells a swipe from a tap, and a sideways swipe from a vertical one.
const DECIDE_PX = 6;
// How far the open panel has to be pushed back before it folds on release.
const CLOSE_PX = 56;
// A quick flick decides on its own, however short it is.
const FLICK_PX_PER_MS = 0.35;
// Speed is read from the last moves only.
const VELOCITY_WINDOW_MS = 80;

/**
 * @param {object} options
 * @param {import('vue').Ref<HTMLElement|null>} options.panelRef the menu panel
 * @param {import('vue').Ref<HTMLElement|null>} options.stripRef the strip button
 * @param {() => void} [options.onClose] closes anything the menu opened (its submenus)
 */
export function useMenuStrip({ panelRef, stripRef, onClose }) {
	// Read before the first render: switching later would slide the full menu away on every load.
	const media = typeof window !== 'undefined' && typeof window.matchMedia === 'function'
		? window.matchMedia(MENU_STRIP_QUERY)
		: null;
	const enabled = ref(Boolean(media?.matches));
	const open = ref(false);
	// While a finger moves the panel: its offset from the open position (0 open, -width closed).
	const trackOffset = ref(null);
	// The items rise in only when a tap opens the menu; a panel pulled out by hand shows them already.
	const riseItems = ref(false);

	let edge = null;
	let swipe = null;
	let suppressStripClickUntil = 0;
	let suppressPanelClickUntil = 0;

	const now = () => (typeof performance !== 'undefined' ? performance.now() : Date.now());
	const panelWidth = () => panelRef.value?.offsetWidth || PANEL_WIDTH;

	const startGesture = (event) => ({
		id: event.pointerId,
		x: event.clientX,
		y: event.clientY,
		axis: null,
		samples: [{ x: event.clientX, time: now() }],
	});

	const record = (gesture, x) => {
		const time = now();
		gesture.samples.push({ x, time });
		while (gesture.samples.length > 2 && time - gesture.samples[0].time > VELOCITY_WINDOW_MS) gesture.samples.shift();
	};

	const velocityOf = (gesture) => {
		const first = gesture.samples[0];
		const last = gesture.samples[gesture.samples.length - 1];
		const elapsed = last.time - first.time;
		return elapsed > 0 ? (last.x - first.x) / elapsed : 0;
	};

	const capture = (event) => {
		try {
			event.currentTarget?.setPointerCapture?.(event.pointerId);
		} catch {
			// Synthetic pointers cannot be captured; touch pointers stay with their target anyway.
		}
	};

	const show = ({ byTap }) => {
		riseItems.value = byTap;
		trackOffset.value = null;
		open.value = true;
	};

	const close = () => {
		edge = null;
		swipe = null;
		trackOffset.value = null;
		riseItems.value = false;
		open.value = false;
		onClose?.();
	};

	const onStripPointerDown = (event) => {
		if (!enabled.value || (event.pointerType === 'mouse' && event.button !== 0)) return;
		event.preventDefault();
		capture(event);
		edge = startGesture(event);
	};

	const onStripPointerMove = (event) => {
		if (!edge || event.pointerId !== edge.id) return;
		const dx = event.clientX - edge.x;
		const dy = event.clientY - edge.y;
		if (!edge.axis) {
			if (Math.max(Math.abs(dx), Math.abs(dy)) < DECIDE_PX) return;
			edge.axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
		}
		if (edge.axis !== 'x') return;
		record(edge, event.clientX);
		// The panel's edge follows the finger out of the strip.
		trackOffset.value = Math.min(0, Math.max(-panelWidth(), dx - panelWidth()));
	};

	const onStripPointerUp = (event) => {
		if (!edge || event.pointerId !== edge.id) return;
		const gesture = edge;
		edge = null;
		suppressStripClickUntil = now() + 500;
		if (gesture.axis === 'y') return; // a slide along the edge is not for the menu
		if (gesture.axis === 'x') {
			const pushedBack = event.clientX - gesture.x <= 0 || velocityOf(gesture) < -FLICK_PX_PER_MS;
			if (pushedBack) {
				trackOffset.value = null;
				return;
			}
		}
		// A tap, or a swipe of any length: the menu opens the rest of the way.
		show({ byTap: gesture.axis === null });
	};

	const onStripPointerCancel = (event) => {
		if (!edge || event.pointerId !== edge.id) return;
		edge = null;
		trackOffset.value = null;
	};

	// Keyboard and assistive technology open the menu with a plain click.
	const onStripClick = (event) => {
		if (now() < suppressStripClickUntil) return;
		if (open.value) {
			close();
			return;
		}
		show({ byTap: true });
		if (event?.detail === 0) {
			requestAnimationFrame(() => panelRef.value?.querySelector('.menu-item:not(:disabled)')?.focus());
		}
	};

	// Swiping the open panel to the left pushes it back with the finger.
	const onPanelPointerDown = (event) => {
		if (!enabled.value || !open.value || event.pointerType === 'mouse') return;
		swipe = startGesture(event);
	};

	const onPanelPointerMove = (event) => {
		if (!swipe || event.pointerId !== swipe.id) return;
		const dx = event.clientX - swipe.x;
		const dy = event.clientY - swipe.y;
		if (!swipe.axis) {
			if (Math.max(Math.abs(dx), Math.abs(dy)) < DECIDE_PX) return;
			if (Math.abs(dy) >= Math.abs(dx) || dx > 0) {
				swipe = null; // scrolling the list, or a finger heading nowhere useful
				return;
			}
			swipe.axis = 'x';
			capture(event);
		}
		record(swipe, event.clientX);
		trackOffset.value = Math.min(0, dx + DECIDE_PX);
	};

	const onPanelPointerUp = (event) => {
		if (!swipe || event.pointerId !== swipe.id) return;
		const gesture = swipe;
		swipe = null;
		if (!gesture.axis) return;
		// The finger lifts over an item; that is not a tap on it.
		suppressPanelClickUntil = now() + 400;
		if ((trackOffset.value ?? 0) < -CLOSE_PX || velocityOf(gesture) < -FLICK_PX_PER_MS) close();
		else trackOffset.value = null;
	};

	// Taps in the open menu run the item's own handler, then the menu folds back.
	// Submenu parents (data-strip-submenu) only show their submenu, so the menu stays.
	const onPanelClickCapture = (event) => {
		if (!enabled.value) return;
		if (now() < suppressPanelClickUntil) {
			event.preventDefault();
			event.stopPropagation();
			return;
		}
		const button = event.target?.closest?.('button');
		if (!button || button.disabled || button.dataset.stripSubmenu) return;
		setTimeout(close, 0);
	};

	const onKeydown = (event) => {
		if (event.key !== 'Escape' || !open.value) return;
		close();
		stripRef?.value?.focus();
	};

	// iOS would otherwise start a scroll, a text selection or a callout under the finger.
	const preventTouchDefault = (event) => {
		if (enabled.value) event.preventDefault();
	};

	const syncMedia = () => {
		enabled.value = Boolean(media?.matches);
		if (!enabled.value) close();
	};

	watch(open, (isOpen) => {
		if (isOpen) document.addEventListener('keydown', onKeydown);
		else document.removeEventListener('keydown', onKeydown);
	});

	watch(
		() => stripRef?.value,
		(strip, previous) => {
			previous?.removeEventListener('touchstart', preventTouchDefault);
			strip?.addEventListener('touchstart', preventTouchDefault, { passive: false });
		},
		{ flush: 'post' },
	);

	onMounted(() => {
		media?.addEventListener?.('change', syncMedia);
	});

	onBeforeUnmount(() => {
		media?.removeEventListener?.('change', syncMedia);
		document.removeEventListener('keydown', onKeydown);
		stripRef?.value?.removeEventListener('touchstart', preventTouchDefault);
	});

	return {
		enabled,
		open,
		trackOffset,
		riseItems,
		close,
		onStripPointerDown,
		onStripPointerMove,
		onStripPointerUp,
		onStripPointerCancel,
		onStripClick,
		onPanelPointerDown,
		onPanelPointerMove,
		onPanelPointerUp,
		onPanelClickCapture,
	};
}
